export function hexToRgb(hex) {
  const parsed = (hex ?? "").toString().replace("#", "");
  const full = parsed.length === 3 ? parsed.split("").map((c) => c + c).join("") : parsed.padEnd(6, "0").slice(0, 6);
  const int = parseInt(full, 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

export function rgbToHex({ r, g, b }) {
  return `#${[r, g, b].map((c) => Math.round(Math.min(255, Math.max(0, c))).toString(16).padStart(2, "0")).join("")}`;
}

// Mixes a hex colour towards white so it keeps its hue but reads as a soft, pastel tint.
export function lighten(hex, amount) {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex({
    r: r + (255 - r) * amount,
    g: g + (255 - g) * amount,
    b: b + (255 - b) * amount,
  });
}

// Mixes a hex colour towards black.
export function darken(hex, amount) {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex({
    r: r * (1 - amount),
    g: g * (1 - amount),
    b: b * (1 - amount),
  });
}

// Returns black or white - whichever gives better readability against the given hex colour.
export function getContrastColor(hex) {
  const { r, g, b } = hexToRgb(hex);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? "#111827" : "#ffffff";
}

// A small, evenly-spaced palette of distinct hues (deliberately not pastel/muted) used to give
// otherwise-neutral list items - e.g. Harvest project names, which unlike Forecast's projects
// don't come with a colour from the API - a stable colour of their own, so a project reads as
// "the same colour" everywhere it appears without needing to store a colour anywhere.
const HASH_COLOR_PALETTE = [
  "#f03e3e", // red
  "#f76707", // orange
  "#f59f00", // yellow
  "#66a80f", // lime
  "#2f9e44", // green
  "#0ca678", // teal
  "#1098ad", // cyan
  "#1c7ed6", // blue
  "#4263eb", // indigo
  "#7048e8", // violet
  "#ae3ec9", // grape
  "#e64980", // pink
];

// Deterministically maps any string to one of the colours above - same input always gives the
// same colour, with no external state to keep in sync.
export function colorFromString(value) {
  const str = (value ?? "").toString();
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return HASH_COLOR_PALETTE[Math.abs(hash) % HASH_COLOR_PALETTE.length];
}

// Builds a 10-shade Mantine colour array (index 0 = lightest, index 9 = darkest) from a single
// hex colour, matching the shape Mantine expects for theme.colors.<name>. Mantine's default
// components (Button, Switch, Tabs, etc.) use shade 6 as their "solid" colour, so the given hex
// is placed there unchanged and the rest of the scale is generated around it.
export function buildColorShades(hex) {
  return [
    lighten(hex, 0.85),
    lighten(hex, 0.7),
    lighten(hex, 0.55),
    lighten(hex, 0.4),
    lighten(hex, 0.25),
    lighten(hex, 0.12),
    hex,
    darken(hex, 0.1),
    darken(hex, 0.2),
    darken(hex, 0.3),
  ];
}
